import {Injectable} from '@angular/core';
import {ConfigurationService, DatasetItem, EnginDatatableDetails} from '@core/interfaces/common/configuration';
import {take} from 'rxjs/operators';
import {forkJoin} from 'rxjs';
import {APIResponse} from '@core/interfaces/system/system-common';

@Injectable()
export class UploadFileService {
    private WORD_SEPARATORS = /[-,._ ]/gm;

    private dataTables: EnginDatatableDetails[];
    private datasets: DatasetItem[] = [];

    constructor(private configurationService: ConfigurationService) {}

    private predictFileDatasetType(predictedDataTable: string, fileTokensAll: string[][]): DatasetItem {
        // If we were able to predict the table name, we know the dataset type is the one that corresponds to that table name
        if (predictedDataTable && predictedDataTable !== 'Multiple') {
            let possibleDataset = this.dataTables.find(
                (dataTable) => predictedDataTable === dataTable.tableNameLogical,
            ).datasetType;
            if (possibleDataset) {
                return this.datasets.find((dataset) => dataset.code.toLowerCase() === possibleDataset.toLowerCase());
            }
        }
        for (let fileTokens of fileTokensAll) {
            for (let fileToken of fileTokens) {
                let possibleDataset = this.datasets.find(
                    (dataset) => dataset.code.toLowerCase() === fileToken.toLowerCase(),
                );
                if (possibleDataset) {
                    return possibleDataset;
                }
            }
        }
    }

    private predictFileDataTable(filename: string, fileTokensAll: string[][], isLive: boolean = false): string {
        let fileFormat = filename.split('.').pop();
        if (fileFormat === 'xlsx' && !isLive) {
            return 'Multiple';
        }
        for (let fileTokens of fileTokensAll) {
            for (let fileToken of fileTokens) {
                let possibleDataTable = this.dataTables.find(
                    (table) => table.tableNameLogical.toLowerCase() === fileToken.toLowerCase(),
                );
                if (possibleDataTable) {
                    return possibleDataTable.tableNameLogical;
                }
            }
        }
    }

    public predictFileDetails(filename: string): Promise<{datasetName: string; dataTableName: string}> {
        return new Promise((resolve) => {
            forkJoin({
                dataTables: this.configurationService.getAllTables().pipe(take(1)),
                datasets: this.configurationService.getDatasets().pipe(take(1)),
            }).subscribe({
                next: (results) => {
                    resolve(this.makeFilePrediction(results, filename));
                },
            });
        });
    }

    public predictLiveFileDetails(filename: string): Promise<{datasetName: string; dataTable: EnginDatatableDetails}> {
        return new Promise((resolve) => {
            forkJoin({
                dataTables: this.configurationService.getPersistedDatasetTableAllList().pipe(take(1)),
                datasets: this.configurationService.getPersistedDatasetList().pipe(take(1)),
            }).subscribe({
                next: (results) => {
                    let predictedDetails = this.makeFilePrediction(results, filename, true);
                    let predictedTable = this.dataTables.find(
                        (table) => table.tableNamePhysical === predictedDetails.dataTableName,
                    );
                    resolve({datasetName: predictedDetails.datasetName, dataTable: predictedTable});
                },
            });
        });
    }

    private makeFilePrediction(
        results: {
            dataTables: APIResponse<EnginDatatableDetails[]>;
            datasets: APIResponse<DatasetItem[]>;
        },
        filename: string,
        isLive: boolean = false,
    ): {datasetName: string; dataTableName: string} {
        this.dataTables = results.dataTables.response;
        this.datasets = results.datasets.response;
        const fileTokensAll: string[][] = this.parseFileName(filename);
        const predictedDataTableName: string = this.predictFileDataTable(filename, fileTokensAll, isLive);
        const predictedDatasetName: DatasetItem = this.predictFileDatasetType(predictedDataTableName, fileTokensAll);
        return {datasetName: predictedDatasetName?.code, dataTableName: predictedDataTableName};
    }

    private parseFileName(filename: string): string[][] {
        filename = filename.slice(0, filename.lastIndexOf('.'));
        const fileTokensWithUnderscores = filename.split(this.WORD_SEPARATORS).filter((name) => name != '');
        const wordTokensWithUnderscores = this.combineTokens(fileTokensWithUnderscores);
        return [wordTokensWithUnderscores, fileTokensWithUnderscores];
    }

    private combineTokens = (filenameTokens: string[]): string[] => {
        // This function creates every combination of side-by-side words such that you can match any dropped file name to a table
        // name regardless of its length
        let newTokens: Set<string> = new Set<string>();
        for (let i: number = 0; i < filenameTokens.length; i++) {
            for (let j: number = i + 1; j <= filenameTokens.length; j++) {
                let token = filenameTokens.slice(i, j).join('_');
                newTokens.add(token);
            }
        }
        // Array is sorted such that more specific values will be prioritized over less specific ones
        return Array.from(newTokens.values()).sort((a, b) => b.length - a.length);
    };
}
